import { SIGNATURE } from '@/constants/config';
import { SIDEBAR_MENU_ITEMS } from '@/constants/menu';
import { CSR_PARSED_RAW_TYPES, CSR_PARSED_TYPES } from '@/constants/ssl';
import { sign as jwtSign } from 'jsonwebtoken';
import jsrsasign from 'jsrsasign';
import keyBy from 'lodash/keyBy';

export const cookieParse = (value) => {
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

// Continue URL utils
export const generateContinueUrl = (route) => {
    const { params, query, name } = route;
    return encodeBase64(
        JSON.stringify({
            name,
            params,
            query,
        })
    );
};

export const getContinueRoute = (route) => {
    const continueUrl = route.query?.continueUrl || '';
    const continueUrlName = continueUrl
        ? JSON.parse(decodeBase64(continueUrl))?.name
        : '';
    return {
        continueUrl,
        continueUrlName,
    };
};

export const prepareRouteToLogin = (route, needContinueUrl = true) => {
    let continueUrl;
    if (needContinueUrl) {
        continueUrl = generateContinueUrl(route);
    }

    return {
        name: 'login',
        query: {
            continueUrl,
        },
    };
};

export const encodeBase64 = (str) => {
    const encodedBase64 = btoa(encodeURIComponent(str));
    return replaceSubstrings(encodedBase64, '+/=', '-_,');
};

export const decodeBase64 = (str) => {
    return decodeURIComponent(atob(replaceSubstrings(str, '-_,', '+/=')));
};

export const generateEncodedMetadata = (metadata) => {
    return encodeBase64(JSON.stringify(metadata));
};

export const decodeMetadata = (metadata) => {
    let result = {};
    if (metadata) {
        try {
            result = JSON.parse(decodeBase64(metadata));
        } catch {
            result = {};
        }
    }
    return result;
};

/**
 * Translate characters or replace substrings
 * @see https://obvcode.blogspot.com/2008/05/strtr-for-javascript.html
 * @param source
 * @param from
 * @param to
 * @returns {string}
 */
export const replaceSubstrings = (source, from, to) => {
    let result = '';
    let indexOfCharacter = 0;
    for (let i = 0; i < source.length; i++) {
        indexOfCharacter = from.indexOf(source.charAt(i));
        if (indexOfCharacter >= 0) {
            result = result + to.charAt(indexOfCharacter);
        } else {
            result += source.charAt(i);
        }
    }
    return result;
};

export const parseCsr = (csr) => {
    try {
        const parsedCsr = jsrsasign.asn1.csr.CSRUtil.getParam(csr.trim());
        const csrInfo = parsedCsr.subject.array.map((i) => {
            const info = i[0];
            const valueType = CSR_PARSED_RAW_TYPES[info.type];
            return {
                ...info,
                valueType,
                label: CSR_PARSED_TYPES[valueType].label,
                translateKey: CSR_PARSED_TYPES[valueType].translateKey,
            };
        });
        const csrAdvancedInfo = [];
        parsedCsr?.extreq?.forEach((e) => {
            const valueType = CSR_PARSED_RAW_TYPES[e.extname];
            if (valueType && CSR_PARSED_TYPES[valueType]) {
                csrAdvancedInfo.push({
                    ...e,
                    valueType,
                    label: CSR_PARSED_TYPES[valueType].label,
                    translateKey: CSR_PARSED_TYPES[valueType].translateKey,
                });
            }
        });
        return {
            valid: true,
            data: keyBy([...csrInfo, ...csrAdvancedInfo], 'valueType'),
        };
    } catch {
        return {
            valid: false,
            data: null,
        };
    }
};

export function getSidebarMenuItemLinkByLinkName(name) {
    const menuItem = SIDEBAR_MENU_ITEMS.find((item) => item.link.name === name);
    return menuItem?.link || {};
}

export const generateSignature = () => {
    const signatureKey = process.env.VUE_APP_SIGNATURE_KEY;
    let signature;
    if (signatureKey) {
        signature = jwtSign({ message: SIGNATURE.MESSAGE }, signatureKey, {
            expiresIn: SIGNATURE.EXPIRED_SECOND,
        });
    }
    return signature;
};
